/**
 * A motion graphic a model wrote, and the rules that make it renderable.
 *
 * The model returns one self-contained HTML page. The only thing Veyso asks of
 * it is a single function:
 *
 *     window.render(t)   // t in seconds, 0 <= t < duration
 *
 * Veyso calls that once per frame and screenshots the result. Which means the
 * page must draw a *function of t* and nothing else — every frame has to be
 * reproducible from its time alone, because the renderer does not play the
 * page, it samples it. A page that animates itself is not slightly wrong; it
 * is sampled at whatever wall-clock moment the screenshot happened to land on,
 * and the output judders in a way that looks like a broken encoder.
 *
 * That single constraint is what this file exists to state once and enforce
 * three times: in the prompt the model is given, in the validation of what
 * comes back, and in the shim that drives it.
 */

export const generatedVideoWidth = 1920;
export const generatedVideoHeight = 1080;

/**
 * 30, not 60.
 *
 * Motion graphics carry fine at 30 and it halves the frame count — 52 seconds
 * is 1,560 frames instead of 3,120, and every one of them is a screenshot.
 */
export const generatedVideoFps = 30;

/** Long enough to tell a story, short enough that a failure is cheap. */
export const generatedVideoMaxSeconds = 70;

/** A page this large is a runaway generation, not a video. */
export const generatedVideoMaxBytes = 600_000;

/**
 * What a page may not contain, and why.
 *
 * Every entry here breaks frame sampling or reaches outside the sandbox. The
 * reason travels with the pattern because it is shown to the model when a page
 * is rejected and it is the only thing that makes a retry better than a
 * re-roll.
 */
export interface PageRule {
  pattern: RegExp;
  reason: string;
  /**
   * Which view of the page the pattern is matched against.
   *
   * "code" has comments *and* string literals removed, which is right for the
   * determinism rules: a page that explains why it avoids `setInterval` is a
   * better page, not a rejected one.
   *
   * "markup" has only comments removed, because an HTML attribute value *is* a
   * string literal — stripping those erased `href="https://fonts.googleapis…"`
   * before the rule could see it, and a Google Fonts link is the single
   * likeliest thing a model emits that cannot work here.
   */
  scan: "code" | "markup";
}

export const generatedPageRules: PageRule[] = [
  {
    pattern: /\brequestAnimationFrame\b/,
    reason:
      "requestAnimationFrame drives animation from wall-clock time. Frames are sampled, not played — draw from the t passed to render(t) instead.",
    scan: "code",
  },
  {
    pattern: /\bset(Interval|Timeout)\s*\(/,
    reason:
      "setInterval and setTimeout make what is on screen depend on when the screenshot happened. Every value must be computed from t.",
    scan: "code",
  },
  {
    pattern: /\b(performance\s*\.\s*now|Date\s*\.\s*now|new\s+Date)\b/,
    reason: "Reading the clock makes the render non-reproducible. t is the only time that exists.",
    scan: "code",
  },
  {
    pattern: /@keyframes\b/,
    reason:
      "A CSS @keyframes animation runs on its own clock, independent of render(t), so it is sampled at an arbitrary phase on every frame.",
    scan: "code",
  },
  {
    pattern: /(^|[;{\s])transition\s*:/,
    reason:
      "A CSS transition smears a change across real time. render(t) sets the final value for that frame; a transition means the screenshot catches it partway.",
    scan: "code",
  },
  {
    pattern: /(^|[;{\s])animation\s*:/,
    reason: "CSS animation runs on wall-clock time. All motion must come from render(t).",
    scan: "code",
  },
  {
    pattern: /\b(fetch|XMLHttpRequest|importScripts|WebSocket|EventSource)\s*\(/,
    reason: "The render container has no outbound network. Everything the page needs must be inside the page.",
    scan: "code",
  },
  {
    pattern: /(src|href)\s*=\s*["']?(https?:)?\/\//i,
    reason:
      "External URLs cannot be fetched — there is no network. Fonts are provided; images must be inline SVG or a data: URI.",
    scan: "markup",
  },
  {
    pattern: /url\(\s*["']?(https?:)?\/\//i,
    reason: "External URLs in CSS cannot be fetched. Use inline SVG or a data: URI.",
    scan: "markup",
  },
];

export interface PageProblem {
  rule: string;
  reason: string;
}

export interface PageCheck {
  ok: boolean;
  problems: PageProblem[];
}

/**
 * Comments and string contents removed, so a rule matches code rather than
 * prose.
 *
 * A page that explains in a comment why it avoids `setInterval` is a *good*
 * page, and rejecting it for saying so is the failure mode a source check
 * always has. Strings go too: a label reading "no transition" is copy, not a
 * CSS declaration.
 */
export function withoutComments(html: string): string {
  return html
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/\/\*[\s\S]*?\*\//g, " ")
    .replace(/(^|[^:/])\/\/[^\n]*/g, "$1 ");
}

export function strippedPage(html: string): string {
  return withoutComments(html)
    .replace(/"(?:[^"\\\n]|\\.)*"/g, '""')
    .replace(/'(?:[^'\\\n]|\\.)*'/g, "''");
}

/**
 * Whether a page can be rendered.
 *
 * Every problem is collected rather than stopping at the first, because the
 * whole list is what a retry needs — fixing one rule and failing the next is
 * two generations where one would do.
 */
export function checkGeneratedPage(html: string): PageCheck {
  const problems: PageProblem[] = [];
  const markup = withoutComments(html);
  const code = strippedPage(html);

  if (html.length > generatedVideoMaxBytes) {
    problems.push({
      rule: "size",
      reason: `The page is ${html.length} bytes; the limit is ${generatedVideoMaxBytes}.`,
    });
  }

  /**
   * The one thing that must be present rather than absent.
   *
   * Matched on the markup view, which keeps string literals, so
   * `window["render"] = …` is recognised. Comments are still gone, so a page
   * that merely mentions render() in a comment does not pass.
   *
   * The distinction between declarations is not pedantry — it is the whole
   * check. A top-level `function` or `var` becomes a property of `window`; a
   * top-level `const` or `let` does not. So a page can define a perfectly good
   * `render` that the shim cannot call, and it fails at frame zero with
   * nothing to suggest why.
   */
  const definesRender =
    /\bfunction\s+render\s*\(/.test(markup) ||
    /\bvar\s+render\s*=/.test(markup) ||
    /\bwindow\s*\.\s*render\s*=/.test(markup) ||
    /\bwindow\s*\[\s*["']render["']\s*\]\s*=/.test(markup);

  if (!definesRender) {
    const blockScoped = /\b(const|let)\s+render\s*=/.test(markup);
    problems.push({
      rule: "render",
      reason: blockScoped
        ? "render is declared with `const` or `let`, which does not put it on `window` — so the page defines a function Veyso cannot call. Add `window.render = render;` after it, or declare it as `function render(t)`."
        : "The page must define render(t) and it must be reachable as `window.render` — write `function render(t) { … }` at the top level, or assign `window.render = …`. Nothing else is ever called.",
    });
  }

  for (const rule of generatedPageRules) {
    const subject = rule.scan === "markup" ? markup : code;
    if (rule.pattern.test(subject)) problems.push({ rule: String(rule.pattern), reason: rule.reason });
  }

  return { ok: problems.length === 0, problems };
}

/** The start of each log line a sweep reports a word on screen with. */
export const screenTextLogPrefix = "veyso-screen-text ";

/**
 * The words on a generated page, matched without touching its code.
 *
 * A film's words are read off the screen while the page is swept, and a
 * language's film swaps each one for its translation after render(t) has
 * drawn it — and puts the English back before the next render(t), so the
 * page's own code only ever sees what it wrote. Nothing in the page is
 * edited, so a translation cannot break it.
 *
 * A text is keyed by its shape: numbers become {1}, {2}… so a counter that
 * runs from 1 to 1,204 is one text, not a thousand; a sentence with words
 * marked inside it is one text with tags ("Ship <a>faster</a> today"), so it
 * is translated whole. The pure matching is here, as plain script, so the
 * tests can run exactly what the page runs.
 *
 * What it cannot reach stays in English: a form field's value, CSS
 * `content`, a shadow root, a canvas drawn before the shim is installed. And
 * the page lays itself out in English — a caret or an underline placed by
 * measuring a word sits where the English word was.
 */
export const screenWordsCore = `
var veysoWords = (function () {
  var NUMBER = /\\d+(?:[.,:]\\d+)*/g;
  var MARK = /<\\/?([a-z])>/g;
  /** A typing cursor at the end of a text: not a word, kept as it is. */
  var CURSOR = /\\s*[|▌█▍▏_]$/;
  function norm(text) { return String(text).replace(/\\s+/g, " ").trim(); }
  function hasLetters(text) { return /\\p{L}/u.test(text); }
  /** The text and the cursor after it, apart. */
  function cursor(text) {
    var found = CURSOR.exec(text);
    return found && found.index > 0 ? { text: text.slice(0, found.index), cursor: found[0] } : { text: text, cursor: "" };
  }
  /** The text with its numbers taken out, and the numbers. Tags (<a>…</a>) are not numbers. */
  function shape(text) {
    var nums = [];
    var key = text.replace(NUMBER, function (found) { nums.push(found); return "{" + nums.length + "}"; });
    return { key: key, nums: nums };
  }
  /** The translation with the numbers put back; null when it lost one. */
  function fill(translation, nums) {
    for (var i = 1; i <= nums.length; i += 1) if (translation.indexOf("{" + i + "}") === -1) return null;
    return translation.replace(/\\{(\\d+)\\}/g, function (all, n) { return nums[n - 1] !== undefined ? nums[n - 1] : all; });
  }
  /**
   * A marked sentence's pieces: the words outside the tags, in the gaps
   * around them, and the words inside each — or null unless every tag of the
   * English is there once, in order, unnested.
   */
  function pieces(translation, count) {
    var gaps = [];
    var inner = [];
    var at = 0;
    for (var i = 0; i < count; i += 1) {
      var letter = String.fromCharCode(97 + i);
      var open = translation.indexOf("<" + letter + ">", at);
      if (open === -1) return null;
      var close = translation.indexOf("</" + letter + ">", open);
      if (close === -1) return null;
      gaps.push(translation.slice(at, open));
      inner.push(translation.slice(open + 3, close));
      at = close + 4;
    }
    gaps.push(translation.slice(at));
    var all = gaps.concat(inner).join("");
    if (/<\\/?[a-z]>/.test(all)) return null;
    return { gaps: gaps, inner: inner };
  }
  function tagCount(text) {
    var count = 0;
    text.replace(/<([a-z])>/g, function () { count += 1; return ""; });
    return count;
  }
  /** Whether a translation keeps what the screen needs: every number and every tag. */
  function keeps(english, translation) {
    if (fill(translation, shape(english).nums) === null) return false;
    var tags = tagCount(english);
    return tags === 0 || pieces(translation, tags) !== null;
  }
  /** A whole text in the language — no typing share — or null. */
  function exact(words, text) {
    var found = shape(text);
    var translation = words.map[found.key];
    return typeof translation === "string" ? fill(translation, found.nums) : null;
  }
  /**
   * A text on screen in the language, or null to leave it. A text that was
   * seen being typed shows the same share of its translation while it is
   * typed — only that text, and only when the start matches no other.
   */
  function lookup(words, text) {
    var found = shape(text);
    var translation = words.map[found.key];
    if (typeof translation === "string") return fill(translation, found.nums);
    if (found.nums.length || text.length < 2) return null;
    var match = null;
    for (var i = 0; i < words.typed.length; i += 1) {
      var key = words.typed[i];
      if (key.length > text.length && key.indexOf(text) === 0) {
        if (match !== null) return null;
        match = key;
      }
    }
    if (match === null) return null;
    var whole = Array.from(words.map[match]);
    var share = Math.max(1, Math.round((whole.length * Array.from(text).length) / Array.from(match).length));
    return whole.slice(0, share).join("");
  }
  /**
   * Words from [english, translation, typed] entries. A text kept as it is
   * stays in, so it is matched as itself; one whose translation lost a
   * number or a tag is left in English.
   */
  function make(entries) {
    var map = Object.create(null);
    var typed = [];
    for (var i = 0; i < entries.length; i += 1) {
      var english = entries[i][0];
      var translation = entries[i][1];
      if (typeof english !== "string" || typeof translation !== "string") continue;
      map[english] = keeps(english, translation) ? translation : english;
      if (entries[i][2] && english.indexOf("{") === -1 && english.indexOf("<") === -1) typed.push(english);
    }
    return { map: map, typed: typed };
  }
  /**
   * The texts a sweep kept: each with whether it was seen being typed; a
   * text seen only on its way to a longer one at the same place is dropped.
   */
  function settle(seen, passing, typed) {
    return seen
      .filter(function (text) {
        if (passing.indexOf(text) === -1) return true;
        return !seen.some(function (other) {
          return other !== text && typed.indexOf(other) !== -1 && other.indexOf(text) === 0;
        });
      })
      .map(function (text) { return { text: text, typed: typed.indexOf(text) !== -1 }; });
  }
  return {
    norm: norm, hasLetters: hasLetters, cursor: cursor, shape: shape, fill: fill, pieces: pieces,
    keeps: keeps, exact: exact, lookup: lookup, make: make, settle: settle, CURSOR: CURSOR,
  };
})();
`;

/** A text read off a page's screen: numbers as {1}, marked words as <a>…</a>; typed when it was seen being typed out. */
export interface ScreenWord {
  text: string;
  typed: boolean;
}

/** A version's words read off the screen, as stored (a bare string is a text never seen typed). */
export function readScreenWords(value: unknown): ScreenWord[] | null {
  if (!Array.isArray(value)) return null;
  return value.flatMap((one): ScreenWord[] => {
    if (typeof one === "string") return [{ text: one, typed: false }];
    if (one && typeof one === "object" && typeof (one as ScreenWord).text === "string") {
      return [{ text: (one as ScreenWord).text, typed: (one as ScreenWord).typed === true }];
    }
    return [];
  });
}

/** How many characters a viewer reads in a screen text: its tags are not read. */
export const screenWordLength = (text: string): number => [...text.replace(/<\/?[a-z]>/g, "")].length;

let matching: { keeps: (english: string, translation: string) => boolean } | null = null;
/**
 * Whether a translation keeps what its place on screen needs — every {n}
 * number and every <a>…</a> tag of the English, in order. The page's own
 * matching, so what is accepted here is what the film can draw.
 */
export function keepsScreenMarks(english: string, translation: string): boolean {
  matching ??= new Function(`${screenWordsCore}; return veysoWords;`)() as typeof matching;
  return matching!.keeps(english, translation);
}

/**
 * The script Veyso injects into every generated page.
 *
 * Deliberately ours and not the model's. The frame protocol is the one part
 * that cannot be slightly wrong — a page that acknowledges the wrong frame, or
 * forgets to acknowledge at all, stalls the whole render — so the model is
 * asked for `render(t)` and nothing more, and this does the talking.
 *
 * The layout read before the reply is what makes the frame real: it forces the
 * browser to flush the style and layout that render(t) just queued, so the
 * screenshot that follows cannot catch the previous frame.
 */
export const generatedPageShim = `
(function () {
  ${screenWordsCore}
  var failed = null;
  var SKIP = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1 };
  /** Elements that sit inside a line of text: a phrase is made of these and the text around them. */
  var INLINE = { SPAN: 1, B: 1, STRONG: 1, EM: 1, I: 1, A: 1, MARK: 1, U: 1, SMALL: 1, SUB: 1, SUP: 1, CODE: 1, S: 1 };
  /** The language's words, once the parent sends them; null draws the English. */
  var words = null;
  /** Undoes every swap, before the next render(t), so the page only ever sees its own words. */
  var undo = [];
  /** What each element is, worked out once a frame. */
  var phrases = null;
  /** During a sweep: the texts seen, those seen only on the way to a longer one, those typed out. */
  var sweep = null;

  function restore() {
    for (var i = undo.length - 1; i >= 0; i -= 1) undo[i]();
    undo = [];
  }
  function setText(node, value) {
    var was = node.nodeValue;
    if (was === value) return;
    undo.push(function () { node.nodeValue = was; });
    node.nodeValue = value;
  }
  function insertText(parent, before, value) {
    var node = document.createTextNode(value);
    parent.insertBefore(node, before);
    undo.push(function () { if (node.parentNode) node.parentNode.removeChild(node); });
  }
  /** An inline element that carries no words: an icon, a dot, a typing caret. Left where it is, out of the text. */
  function wordless(kid) {
    var own = kid.textContent.replace(/\\u00a0/g, " ").trim();
    return own === "" || /^[|▌█▍▏_]$/.test(own);
  }
  /**
   * A phrase spread over several inline elements, read as one text:
   *  - "letters": a word spelled one letter per element (a letter-by-letter animation);
   *  - "run": words in elements, with or without words around them, each
   *    element's words marked ("Ship <a>faster</a> today", "<a>Home</a><b>Pricing</b>"),
   *    so a word-by-word reveal or a row of links is translated whole and
   *    each element still gets its own words.
   * Null for anything else, whose text nodes are read one by one.
   */
  function phrase(holder) {
    if (!holder || SKIP[holder.tagName]) return null;
    if (phrases.has(holder)) return phrases.get(holder);
    var result = null;
    var all = holder.children;
    var kids = [];
    var inline = all.length > 0 && all.length <= 40;
    for (var i = 0; inline && i < all.length; i += 1) {
      if (!INLINE[all[i].tagName] || all[i].children.length) inline = false;
      else if (!wordless(all[i])) kids.push(all[i]);
    }
    var textual = false;
    for (var node = holder.firstChild; node; node = node.nextSibling) {
      if (node.nodeType === 3 && node.nodeValue.trim()) textual = true;
    }
    if (inline && kids.length > 0 && kids.length <= 26 && (textual || kids.length >= 2)) {
      var letters = !textual && kids.every(function (one) {
        return Array.from(one.textContent.replace(/\\u00a0/g, " ")).length === 1;
      });
      if (letters) {
        result = { kind: "letters", kids: kids, key: veysoWords.norm(kids.map(function (one) {
          return one.textContent.replace(/\\u00a0/g, " ");
        }).join("")) };
      } else {
        var key = "";
        for (var at = holder.firstChild; at; at = at.nextSibling) {
          if (at.nodeType === 3) key += at.nodeValue;
          else if (at.nodeType === 1 && kids.indexOf(at) !== -1) {
            var letter = String.fromCharCode(97 + kids.indexOf(at));
            // Spacing inside an element is the layout's: it goes outside the tag.
            var inner = at.textContent.replace(/\\u00a0/g, " ");
            var lead = /^\\s*/.exec(inner)[0];
            var trail = inner.slice(lead.length).match(/\\s*$/)[0];
            key += lead + "<" + letter + ">" + inner.trim() + "</" + letter + ">" + trail;
          }
        }
        result = { kind: "run", kids: kids, key: veysoWords.norm(key) };
      }
    }
    phrases.set(holder, result);
    return result;
  }
  /**
   * Every text on screen, once: visit(text, element, node, null, slot) for a
   * lone text node — slot is its place among its element's texts — and
   * (text, holder, null, phrase, 0) for a phrase.
   */
  function eachText(visit) {
    if (!document.body) return;
    phrases = new Map();
    var done = new Set();
    var slots = new Map();
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    for (var node = walker.nextNode(); node; node = walker.nextNode()) {
      var element = node.parentElement;
      if (!element || SKIP[element.tagName] || element.closest("script,style,noscript,template")) continue;
      var holder = null;
      var found = null;
      if (INLINE[element.tagName] && !element.children.length) {
        found = phrase(element.parentElement);
        if (found && found.kids.indexOf(element) !== -1) holder = element.parentElement;
        else if (found && wordless(element)) continue;
      }
      if (!holder) {
        found = phrase(element);
        if (found && found.kind === "run") holder = element;
      }
      if (holder) {
        if (done.has(holder)) continue;
        done.add(holder);
        visit(found.key, holder, null, found, 0);
        continue;
      }
      if (!node.nodeValue.trim()) continue;
      var slot = slots.get(element) || 0;
      slots.set(element, slot + 1);
      visit(veysoWords.norm(node.nodeValue), element, node, null, slot);
    }
  }
  /** An element's words replaced, its own spacing kept; any further text nodes in it emptied. */
  function writeKid(kid, value) {
    var first = null;
    for (var node = kid.firstChild; node; node = node.nextSibling) {
      if (node.nodeType !== 3) continue;
      if (!first && node.nodeValue.trim()) first = node;
      else if (first) setText(node, "");
    }
    if (!first) {
      if (value) insertText(kid, null, value);
      return;
    }
    var lead = /^[\\s\\u00a0]*/.exec(first.nodeValue)[0];
    var trail = first.nodeValue.slice(lead.length).match(/[\\s\\u00a0]*$/)[0];
    setText(first, lead + value.trim() + trail);
  }
  /** One letter to each element in turn; the last takes what is left, any after it are emptied. */
  function dealLetters(kids, letters) {
    for (var i = 0; i < kids.length; i += 1) {
      writeKid(kids[i], i < kids.length - 1 ? letters[i] || "" : letters.slice(i).join(""));
    }
  }
  /** A marked translation written back: the words between the tags into the gaps, each element's into it. */
  function writeRun(holder, found, translation) {
    var kids = found.kids;
    var split = veysoWords.pieces(translation, kids.length);
    if (!split) return;
    var gap = 0;
    var filled = false;
    for (var node = holder.firstChild; node; node = node.nextSibling) {
      if (node.nodeType === 1 && kids.indexOf(node) !== -1) {
        if (!filled && split.gaps[gap].trim()) insertText(holder, node, split.gaps[gap]);
        gap += 1;
        filled = false;
      } else if (node.nodeType === 3) {
        // Spacing between elements is the layout's; only words are the translation's.
        if (!split.gaps[gap].trim() && !node.nodeValue.trim()) continue;
        setText(node, filled ? "" : split.gaps[gap]);
        filled = true;
      }
    }
    if (!filled && split.gaps[gap].trim()) insertText(holder, null, split.gaps[gap]);
    for (var i = 0; i < kids.length; i += 1) writeKid(kids[i], split.inner[i]);
  }
  /** The words now on screen, in the language. */
  function swap() {
    if (!words) return;
    eachText(function (text, element, node, found) {
      if (!text || !veysoWords.hasLetters(text)) return;
      var apart = veysoWords.cursor(text);
      var translated = veysoWords.lookup(words, apart.text);
      if (translated === null || translated === apart.text) return;
      if (node) {
        var lead = /^\\s*/.exec(node.nodeValue)[0];
        var trail = node.nodeValue.slice(lead.length).match(/\\s*$/)[0];
        setText(node, lead + translated + apart.cursor + trail);
      } else if (found.kind === "letters") {
        dealLetters(found.kids, Array.from(translated.replace(/ /g, "\\u00a0")));
      } else {
        writeRun(element, found, translated);
      }
    });
  }
  /**
   * Notes a text seen in this sample. A text counts once it is on screen in
   * two samples, so a scramble's passing letters are not taken for words.
   * Where it was seen tells typing apart: the same place showing a longer
   * text that starts with it means it was on its way there.
   */
  function note(place, slot, raw) {
    var text = veysoWords.shape(veysoWords.cursor(raw).text).key;
    if (!text || text.length > 400 || !veysoWords.hasLetters(text)) return;
    var counted = sweep.counts.get(text);
    if (!counted) sweep.counts.set(text, { samples: 1, last: sweep.sample });
    else if (counted.last !== sweep.sample) {
      counted.samples += 1;
      counted.last = sweep.sample;
      if (counted.samples === 2 && sweep.seen.indexOf(text) === -1 && sweep.seen.length < 300) sweep.seen.push(text);
    }
    if (place === null) return;
    var texts = sweep.last.get(place) || {};
    var before = texts[slot];
    if (before && text.length > before.length && text.indexOf(before) === 0) {
      if (sweep.passing.indexOf(before) === -1) sweep.passing.push(before);
      if (sweep.typed.indexOf(text) === -1) sweep.typed.push(text);
    }
    texts[slot] = text;
    sweep.last.set(place, texts);
  }
  function shown(element) {
    if (!element.getClientRects().length) return false;
    for (var at = element; at && at !== document.documentElement; at = at.parentElement) {
      var style = getComputedStyle(at);
      if (style.display === "none" || style.visibility === "hidden" || parseFloat(style.opacity) === 0) return false;
    }
    return true;
  }
  function collect() {
    eachText(function (text, element, node, found, slot) {
      if (text && shown(element)) note(element, slot, text);
    });
  }
  // Words drawn on a canvas are matched as they are drawn; a measurement only
  // ever matches a whole text, so a line-wrapping loop measures what it means.
  var canvas = window.CanvasRenderingContext2D && CanvasRenderingContext2D.prototype;
  if (canvas) {
    ["fillText", "strokeText", "measureText"].forEach(function (name) {
      var original = canvas[name];
      if (typeof original !== "function") return;
      canvas[name] = function (text) {
        var args = Array.prototype.slice.call(arguments);
        var plain = veysoWords.norm(text);
        if (sweep && name !== "measureText" && plain.length >= 2) note(null, 0, plain);
        if (words && plain) {
          var translated = name === "measureText" ? veysoWords.exact(words, plain) : veysoWords.lookup(words, plain);
          if (translated !== null) args[0] = translated;
        }
        return original.apply(this, args);
      };
    });
  }
  function draw(time) {
    restore();
    if (typeof window.render !== "function") throw new Error("the page defines no render(t)");
    window.render(time);
    swap();
  }
  function reply(frame) {
    parent.postMessage({ veyso: "drawn", frame: frame, failed: failed }, "*");
  }
  addEventListener("message", function (event) {
    var data = event.data;
    if (!data) return;

    /**
     * Every moment in the film, drawn once, in one page load.
     *
     * Static validation cannot see a runtime fault: a page can pass every rule
     * and still throw at 37.4 seconds because an array ran out. Finding that
     * during the real render means discovering it after hundreds of frames
     * have already been encoded, and finding it by sampling stills means a
     * browser launch per sample. Sweeping inside the page costs one load.
     */
    if (data.words && !words) words = veysoWords.make(data.words);
    if (data.veyso === "sweep") {
      var failures = [];
      // Words are read from the English only: a language's sweep reads nothing.
      sweep = data.collect && !words
        ? { seen: [], passing: [], typed: [], last: new Map(), counts: new Map(), sample: 0 }
        : null;
      for (var i = 0; i < data.times.length; i += 1) {
        try {
          draw(data.times[i]);
          if (sweep) {
            sweep.sample = i;
            collect();
          }
        } catch (error) {
          failures.push({ time: data.times[i], message: String((error && error.message) || error) });
          // The first few are enough to describe the fault. A page broken at
          // one moment is usually broken at every moment after it, and a
          // hundred identical messages help nobody.
          if (failures.length >= 4) break;
        }
      }
      var texts = sweep ? veysoWords.settle(sweep.seen, sweep.passing, sweep.typed) : [];
      sweep = null;
      parent.postMessage({ veyso: "swept", failures: failures, texts: texts }, "*");
      return;
    }

    if (data.veyso !== "draw") return;
    if (failed === null) {
      try {
        draw(data.time);
      } catch (error) {
        failed = String((error && error.message) || error);
      }
    }
    // Flush style and layout before answering, or the screenshot races the
    // frame that was just drawn.
    void document.documentElement.offsetHeight;
    reply(data.frame);
  });
  addEventListener("error", function (event) {
    if (failed === null) failed = String(event.message || "script error");
  });
  // Faces arrive inlined, but decoding still takes a turn of the event loop.
  // Announcing before that means frame 0 is captured in a fallback face while
  // every later frame is correct — one wrong frame, kept forever, and exactly
  // the kind nobody reviews.
  var fonts = document.fonts && document.fonts.ready;
  if (fonts && typeof fonts.then === "function") {
    fonts.then(function () { parent.postMessage({ veyso: "ready" }, "*"); });
  } else {
    parent.postMessage({ veyso: "ready" }, "*");
  }
})();
`;
